import { useEffect, useMemo, useRef, useState } from "react";
import type { OcPortalConfig, OcFlowState } from "../App";
import { ocSendCode, ocVerify } from "../lib/oc-api";

interface OcStep3VerifyProps {
  config: OcPortalConfig;
  state: OcFlowState;
  setState: (next: OcFlowState) => void;
}

function ocGetSlug(): string {
  const segments = window.location.pathname.split("/").filter(Boolean);
  return segments[0] ?? "";
}

const OC_RESEND_COOLDOWN_SECONDS = 30;

export function OcStep3Verify({ config, state, setState }: OcStep3VerifyProps) {
  const slug = useMemo(ocGetSlug, []);
  const [digits, setDigits] = useState<string[]>(["", "", "", "", "", ""]);
  const [pending, setPending] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [expiredEmphasis, setExpiredEmphasis] = useState(false);
  const [cooldown, setCooldown] = useState(OC_RESEND_COOLDOWN_SECONDS);
  const [resending, setResending] = useState(false);
  const [resendNotice, setResendNotice] = useState<string | null>(null);
  const inputsRef = useRef<Array<HTMLInputElement | null>>([]);

  const maskedTarget = useMemo(() => {
    const match = state.allowedChannels?.find((c) => c.channel === state.selectedChannel);
    return match?.target ?? "your recovery channel";
  }, [state.allowedChannels, state.selectedChannel]);

  // Resend cooldown ticker.
  useEffect(() => {
    if (cooldown <= 0) return;
    const id = window.setTimeout(() => setCooldown((c) => c - 1), 1000);
    return () => window.clearTimeout(id);
  }, [cooldown]);

  // Focus first slot on mount.
  useEffect(() => {
    inputsRef.current[0]?.focus();
  }, []);

  const code = digits.join("");

  const ocSubmitCode = async (fullCode: string) => {
    if (pending) return;
    if (!state.sessionId) {
      setErrorMessage("Your session expired. Please start over.");
      return;
    }
    setPending(true);
    setErrorMessage(null);
    try {
      const res = await ocVerify(slug, { sessionId: state.sessionId, code: fullCode });
      if (res.ok) {
        setState({ ...state, step: "password" });
        return;
      }
      if (res.error === "code_invalid") {
        setErrorMessage("That code did not match. Try again.");
        setDigits(["", "", "", "", "", ""]);
        inputsRef.current[0]?.focus();
        return;
      }
      if (res.error === "code_expired") {
        setErrorMessage("Your code expired. Please request a new one.");
        setExpiredEmphasis(true);
        setDigits(["", "", "", "", "", ""]);
        return;
      }
      if (res.error === "session_invalid") {
        setErrorMessage("Your session expired. Please start over.");
        return;
      }
      if (res.error === "locked") {
        setErrorMessage(
          "Too many attempts. For security, please contact your IT helpdesk to reset your password."
        );
        window.setTimeout(() => {
          setState({ step: "identifier" });
        }, 5000);
        return;
      }
      setErrorMessage("Something went wrong. Please try again.");
    } catch {
      setErrorMessage("Something went wrong. Please try again.");
    } finally {
      setPending(false);
    }
  };

  const ocHandleDigitChange = (index: number, raw: string) => {
    const cleaned = raw.replace(/\D/g, "");
    if (!cleaned) {
      // Empty input (e.g., user deleted). Just clear the slot.
      const next = [...digits];
      next[index] = "";
      setDigits(next);
      return;
    }
    // Pasted multi-char input — distribute across slots.
    if (cleaned.length > 1) {
      const next = [...digits];
      let cursor = index;
      for (const ch of cleaned) {
        if (cursor > 5) break;
        next[cursor] = ch;
        cursor += 1;
      }
      setDigits(next);
      const focusAt = Math.min(cursor, 5);
      inputsRef.current[focusAt]?.focus();
      const joined = next.join("");
      if (joined.length === 6 && !joined.includes("")) {
        void ocSubmitCode(joined);
      }
      return;
    }
    // Single digit.
    const next = [...digits];
    next[index] = cleaned;
    setDigits(next);
    if (index < 5) {
      inputsRef.current[index + 1]?.focus();
    }
    const joined = next.join("");
    if (joined.length === 6 && !joined.includes("")) {
      void ocSubmitCode(joined);
    }
  };

  const ocHandleKeyDown = (index: number, e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === "Backspace" && !digits[index] && index > 0) {
      e.preventDefault();
      const next = [...digits];
      next[index - 1] = "";
      setDigits(next);
      inputsRef.current[index - 1]?.focus();
    } else if (e.key === "ArrowLeft" && index > 0) {
      e.preventDefault();
      inputsRef.current[index - 1]?.focus();
    } else if (e.key === "ArrowRight" && index < 5) {
      e.preventDefault();
      inputsRef.current[index + 1]?.focus();
    }
  };

  const ocHandlePaste = (e: React.ClipboardEvent<HTMLInputElement>) => {
    const pasted = e.clipboardData.getData("text").replace(/\D/g, "").slice(0, 6);
    if (!pasted) return;
    e.preventDefault();
    const next = ["", "", "", "", "", ""];
    for (let i = 0; i < pasted.length; i += 1) {
      next[i] = pasted[i];
    }
    setDigits(next);
    const focusAt = Math.min(pasted.length, 5);
    inputsRef.current[focusAt]?.focus();
    if (pasted.length === 6) {
      void ocSubmitCode(pasted);
    }
  };

  const ocHandleResend = async () => {
    if (cooldown > 0 || resending) return;
    if (!state.sessionId || !state.selectedChannel) {
      setErrorMessage("Your session expired. Please start over.");
      return;
    }
    setResending(true);
    setErrorMessage(null);
    setResendNotice(null);
    try {
      const res = await ocSendCode(slug, {
        sessionId: state.sessionId,
        channel: state.selectedChannel,
      });
      if (res.ok) {
        setResendNotice("A new code is on the way.");
        setExpiredEmphasis(false);
        setCooldown(OC_RESEND_COOLDOWN_SECONDS);
        setDigits(["", "", "", "", "", ""]);
        inputsRef.current[0]?.focus();
      } else if (res.error === "session_invalid") {
        setErrorMessage("Your session expired. Please start over.");
      } else {
        setErrorMessage("We could not send a new code. Please try again in a moment.");
      }
    } catch {
      setErrorMessage("We could not send a new code. Please try again in a moment.");
    } finally {
      setResending(false);
    }
  };

  const ocHandleBack = () => {
    setState({ ...state, step: "channel" });
  };

  const ocHandleStartOver = () => {
    setState({ step: "identifier" });
  };

  const sessionExpired =
    errorMessage === "Your session expired. Please start over.";

  return (
    <div className="w-full">
      <h1 className="text-2xl font-semibold text-slate-900">Enter the code we sent</h1>
      <p className="mt-2 text-sm text-slate-600">
        We sent a code to <span className="font-medium text-slate-800">{maskedTarget}</span>. The
        code expires in 10 minutes.
      </p>

      <div className="mt-6 grid grid-cols-6 gap-2">
        {digits.map((d, i) => (
          <input
            key={i}
            ref={(el) => {
              inputsRef.current[i] = el;
            }}
            type="text"
            inputMode="numeric"
            autoComplete="one-time-code"
            maxLength={6}
            value={d}
            onChange={(e) => ocHandleDigitChange(i, e.target.value)}
            onKeyDown={(e) => ocHandleKeyDown(i, e)}
            onPaste={ocHandlePaste}
            disabled={pending}
            className="h-14 w-full rounded-md border border-slate-300 bg-white text-center font-mono text-xl tracking-widest text-slate-900 outline-none transition focus:border-[var(--oc-primary)] focus:ring-2 focus:ring-[var(--oc-primary)]/30 disabled:opacity-60"
            aria-label={`Digit ${i + 1}`}
          />
        ))}
      </div>

      {errorMessage && (
        <div
          className="mt-4 rounded-md border border-amber-200 bg-amber-50 px-3 py-2 text-sm text-amber-800"
          role="alert"
        >
          {errorMessage}
        </div>
      )}

      {resendNotice && !errorMessage && (
        <div className="mt-4 rounded-md border border-emerald-200 bg-emerald-50 px-3 py-2 text-sm text-emerald-800">
          {resendNotice}
        </div>
      )}

      <div className="mt-6 flex items-center justify-between">
        <button
          type="button"
          onClick={ocHandleBack}
          className="text-sm font-medium text-slate-600 hover:text-slate-900"
        >
          Back
        </button>
        <button
          type="button"
          onClick={() => void ocSubmitCode(code)}
          disabled={pending || code.length !== 6}
          className="rounded-md px-4 py-2 text-sm font-semibold text-white shadow-sm transition disabled:opacity-50"
          style={{ backgroundColor: "var(--oc-primary)" }}
        >
          {pending ? "Verifying..." : "Verify"}
        </button>
      </div>

      <div className="mt-6 text-center text-sm">
        <button
          type="button"
          onClick={() => void ocHandleResend()}
          disabled={cooldown > 0 || resending}
          className={
            "font-medium underline-offset-2 transition " +
            (expiredEmphasis
              ? "text-[var(--oc-primary)] font-semibold underline"
              : "text-slate-600 hover:text-slate-900 hover:underline") +
            " disabled:cursor-not-allowed disabled:text-slate-400 disabled:no-underline"
          }
        >
          {cooldown > 0 ? `Resend code in ${cooldown}s` : resending ? "Sending..." : "Resend code"}
        </button>
      </div>

      {sessionExpired && (
        <div className="mt-4 text-center">
          <button
            type="button"
            onClick={ocHandleStartOver}
            className="rounded-md border border-slate-300 bg-white px-4 py-2 text-sm font-medium text-slate-700 hover:bg-slate-50"
          >
            Start over
          </button>
        </div>
      )}
    </div>
  );
}

export default OcStep3Verify;
